import { rgbaToThumbHash } from 'thumbhash';

/**
 * Le thumbhash est l'aperçu flouté affiché par totmobile (`expo-image`, prop `placeholder`) le temps
 * que l'image se charge. Il se calcule ici, dans le NAVIGATEUR, et pas côté API : décoder une image
 * en Node demanderait une bibliothèque native (`sharp`), que le bundle esbuild déployé en production
 * ne peut pas embarquer — l'image du conteneur ne contient que `build/`, sans `node_modules`.
 *
 * L'algorithme attend au plus 100×100 pixels RGBA (cf. la doc de `thumbhash`).
 */
const MAX_SIZE = 100;

const loadImage = (file: Blob): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const objectUrl = URL.createObjectURL(file);
        const image = new Image();

        image.onload = () => {
            URL.revokeObjectURL(objectUrl);
            resolve(image);
        };
        image.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error("Image illisible : impossible d'en calculer l'aperçu"));
        };

        image.src = objectUrl;
    });

export const computeThumbhash = async (file: Blob): Promise<string> => {
    const image = await loadImage(file);

    const scale = Math.min(MAX_SIZE / image.width, MAX_SIZE / image.height, 1);
    const width = Math.max(1, Math.round(image.width * scale));
    const height = Math.max(1, Math.round(image.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) {
        throw new Error("Aperçu impossible : le navigateur n'a pas fourni de contexte 2D");
    }

    context.drawImage(image, 0, 0, width, height);
    const { data } = context.getImageData(0, 0, width, height);

    const hash = rgbaToThumbHash(width, height, data);

    // `expo-image` et le reste du dépôt manipulent le thumbhash en base64.
    return btoa(String.fromCharCode(...hash));
};
